import {
    useState, useEffect, useRef, useMemo,
} from 'react';

export function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(
        () => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => {
                clearTimeout(handler);
            };
        },
        [value, delay],
    );

    return debouncedValue;
}

export function useOnClickOutside(ref, handler) {
    useEffect(
        () => {
            const listener = (event) => {
                if (!ref.current || ref.current.contains(event.target)) {
                    return;
                }

                handler(event);
            };

            document.addEventListener('mousedown', listener);
            document.addEventListener('touchstart', listener);

            return () => {
                document.removeEventListener('mousedown', listener);
                document.removeEventListener('touchstart', listener);
            };
        },
        [ref, handler],
    );
}

export function usePrevious(value) {
    const ref = useRef();

    useEffect(() => {
        ref.current = value;
    });

    return ref.current;
}

export const useDebouncedEffect = (effect, deps, delay) => {
    useEffect(() => {
        const handler = setTimeout(() => effect(), delay);

        return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps || [], delay]);
};

export function useDebouncedCallback(
    func,
    wait,
    options,
) {
    const lastCallTime = useRef(null);
    const lastInvokeTime = useRef(0);
    const timerId = useRef(null);
    const lastArgs = useRef([]);
    const lastThis = useRef();
    const result = useRef();
    const funcRef = useRef(func);
    const mounted = useRef(true);

    funcRef.current = func;

    const useRAF = !wait && wait !== 0 && typeof window !== 'undefined';

    if (typeof func !== 'function') {
        throw new TypeError('Expected a function');
    }

    // eslint-disable-next-line no-param-reassign
    wait = +wait || 0;
    // eslint-disable-next-line no-param-reassign
    options = options || {};

    const leading = !!options.leading;
    const trailing = 'trailing' in options ? !!options.trailing : true; // `true` by default
    const maxing = 'maxWait' in options;
    const maxWait = maxing ? Math.max(+options.maxWait || 0, wait) : null;

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    }, []);

    const debounced = useMemo(() => {
        const invokeFunc = (time) => {
            const args = lastArgs.current;
            const thisArg = lastThis.current;

            lastThis.current = null;
            lastArgs.current = null;
            lastInvokeTime.current = time;

            result.current = funcRef.current.apply(thisArg, args);

            return result.current;
        };

        // eslint-disable-next-line no-shadow
        const startTimer = (pendingFunc, wait) => {
            if (useRAF) cancelAnimationFrame(timerId.current);
            timerId.current = useRAF ? requestAnimationFrame(pendingFunc) : setTimeout(pendingFunc, wait);
        };

        const shouldInvoke = (time) => {
            if (!mounted.current) return false;

            const timeSinceLastCall = time - lastCallTime.current;
            const timeSinceLastInvoke = time - lastInvokeTime.current;

            return (
                !lastCallTime.current
        || timeSinceLastCall >= wait
        || timeSinceLastCall < 0
        || (maxing && timeSinceLastInvoke >= maxWait)
            );
        };

        const trailingEdge = (time) => {
            timerId.current = null;

            if (trailing && lastArgs.current) {
                return invokeFunc(time);
            }

            lastArgs.current = lastThis.current = null;

            return result.current;
        };

        const timerExpired = () => {
            const time = Date.now();

            if (shouldInvoke(time)) {
                return trailingEdge(time);
            }

            if (!mounted.current) {
                return;
            }

            const timeSinceLastCall = time - lastCallTime.current;
            const timeSinceLastInvoke = time - lastInvokeTime.current;
            const timeWaiting = wait - timeSinceLastCall;
            const remainingWait = maxing ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;

            startTimer(timerExpired, remainingWait);
        };

        const func = (...args) => {
            const time = Date.now();
            const isInvoking = shouldInvoke(time);

            lastArgs.current = args;
            lastThis.current = this;
            lastCallTime.current = time;

            if (isInvoking) {
                if (!timerId.current && mounted.current) {
                    lastInvokeTime.current = lastCallTime.current;
                    startTimer(timerExpired, wait);

                    return leading ? invokeFunc(lastCallTime.current) : result.current;
                }

                if (maxing) {
                    startTimer(timerExpired, wait);

                    return invokeFunc(lastCallTime.current);
                }
            }

            if (!timerId.current) {
                startTimer(timerExpired, wait);
            }

            return result.current;
        };

        func.cancel = () => {
            if (timerId.current) {
                useRAF ? cancelAnimationFrame(timerId.current) : clearTimeout(timerId.current);
            }

            lastInvokeTime.current = 0;
            lastArgs.current = lastCallTime.current = lastThis.current = timerId.current = null;
        };

        func.isPending = () => !!timerId.current;

        func.flush = () => (!timerId.current ? result.current : trailingEdge(Date.now()));

        return func;
    }, [leading, maxing, wait, maxWait, trailing, useRAF]);

    return debounced;
}
